import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Alert, Card, CardContent, Stack, Button, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox, FormControlLabel, useMediaQuery, useTheme,
} from '@mui/material';
import { Download, InfoOutlined } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import {
  getMyW2Consent, getMyW2s, giveMyW2Consent, withdrawMyW2Consent, downloadMyW2, downloadW2Sample,
} from '../api';

// Must equal CURRENT_DISCLOSURE_VERSION in server/services/w2DeliveryService.js. Bump both when
// W2_DISCLOSURE changes; the server records the version on every consent row as evidence.
// 2026-10-r3: revised text (flag-off variants, paper after deactivation).
export const W2_DISCLOSURE_VERSION = '2026-10-r3';

// `enabled` = tenant electronic furnishing is on. Off: PDF is only a copy and paper continues.
export const W2_DISCLOSURE = (employer, enabled = true) => [
  { heading: 'Paper unless you consent', text: enabled
    ? `Unless you consent, ${employer} gives you your Form W-2 on paper. Consenting is optional.`
    : `${employer} gives you your official Form W-2 on paper. Consenting to electronic delivery is optional and does not change that until ${employer} turns electronic delivery on.` },
  { heading: 'What you are consenting to', text: enabled
    ? "Receiving your Form W-2 (Wage and Tax Statement) as a PDF in HourTrax instead of on paper. Your consent covers this year's W-2 and every later year until you withdraw it."
    : "Receiving a PDF copy of your Form W-2 in HourTrax. Until your employer turns on electronic delivery of the official form, the PDF is a copy for your records and your official W-2 still comes on paper. Your consent applies once electronic delivery is turned on, and covers every later year until you withdraw it." },
  { heading: 'How you will be told', text: (enabled
    ? 'When a W-2 is posted you get an email with the subject IMPORTANT TAX RETURN DOCUMENT AVAILABLE at the email address on your employee profile, plus an in-app notification. Both say how to open and print it.'
    : 'When a copy of your W-2 is posted you get an email with the subject Your <year> Form W-2 copy is available at the email address on your employee profile, plus an in-app notification. Both say how to open and print it. Once electronic delivery is turned on, the email subject becomes IMPORTANT TAX RETURN DOCUMENT AVAILABLE.')
    + ' If your email address changes, ask your payroll administrator to update it.' },
  { heading: 'What you need', text: 'A device with a current web browser (Chrome, Edge, Safari or Firefox), an internet connection, and a PDF viewer to open, print and save the form. Current browsers and phones include one; Adobe Acrobat Reader is a free alternative. You must sign in to HourTrax with your own account.' },
  { heading: 'How long it stays available', text: 'Your W-2 stays in My Pay at least through October 15 of the year after the tax year (a 2026 W-2 through October 15, 2027) and for as long as your account is active. If your account is deactivated before then, your employer will send you a paper copy of any W-2 you received here. Download a copy for your records.' },
  { heading: 'Paper copies after you consent', text: 'You can ask your payroll administrator for a paper copy at any time. Asking does not withdraw your consent.' },
  { heading: 'Withdrawing your consent', text: enabled
    ? 'Withdraw at any time with Withdraw consent on this screen, or tell your payroll administrator in writing (on paper or by email). A withdrawal takes effect the day it is received — in HourTrax, immediately — and your employer may confirm it in writing. It does not apply to a W-2 already furnished to you electronically; later W-2s come on paper.'
    : 'Withdraw at any time with Withdraw consent on this screen, or tell your payroll administrator in writing (on paper or by email). A withdrawal takes effect the day it is received — in HourTrax, immediately — and your employer may confirm it in writing. Later W-2s come on paper.' },
  { heading: 'When electronic delivery stops', text: 'Electronic delivery stops if your employment ends or your HourTrax account is deactivated; any later W-2 comes on paper.' },
  { heading: 'Corrected forms', text: 'If a W-2 is corrected or reissued, it is posted the same way and you get the same notice.' },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function W2Documents() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [consent, setConsent] = useState(null);
  const [w2s, setW2s] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [consentOpen, setConsentOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [sampleToken, setSampleToken] = useState('');
  const [sampleError, setSampleError] = useState('');
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const employer = user?.tenantName || 'your employer';

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([getMyW2Consent(), getMyW2s()]);
      setConsent(c.data);
      setW2s(l.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load W-2 information');
    }
  }, []);

  useEffect(() => { if (user?.employeeId) load(); }, [user?.employeeId, load]);

  if (!user?.employeeId) return null;

  const openConsent = () => {
    setSampleToken(''); setSampleError(''); setChecked(false); setDialogError('');
    setConsentOpen(true);
  };

  const handleSample = async () => {
    setSampleError('');
    try {
      const token = await downloadW2Sample();
      if (!token) throw new Error('missing sample token');
      setSampleToken(token);
    } catch (err) {
      setSampleToken('');
      setSampleError('Could not download the sample PDF — you cannot consent until a PDF opens on this device');
    }
  };

  const handleConsent = async () => {
    setSaving(true); setDialogError('');
    try {
      await giveMyW2Consent({ disclosure_version: W2_DISCLOSURE_VERSION, sample_token: sampleToken });
      setConsentOpen(false);
      setSuccess('Consent recorded. You will be notified when a W-2 is posted.');
      await load();
    } catch (err) {
      setDialogError(err.response?.data?.error || 'Failed to record consent');
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async () => {
    setSaving(true);
    try {
      await withdrawMyW2Consent();
      setWithdrawOpen(false);
      setSuccess('');
      await load();
    } catch (err) {
      setWithdrawOpen(false);
      setError(err.response?.data?.error || 'Failed to withdraw consent');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (id) => {
    setError('');
    try {
      await downloadMyW2(id);
    } catch (err) {
      setError(err.response?.data?.code === 'NEEDS_RERELEASE' || err.code === 'NEEDS_RERELEASE'
        ? 'This W-2 was corrected and is not yet available. Your employer must release the corrected form; check back soon.'
        : 'Failed to download W-2');
    }
  };

  const status = consent?.status;
  const enabled = !!consent?.electronic_furnishing_enabled;
  const copyLabel = enabled ? '' : ' (copy for your records)';
  const keepNote = (year) => `Keep a copy — available here at least through October 15, ${year + 1}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="h6">Tax Documents (W-2)</Typography>
        <Tooltip title="Form W-2 is your annual Wage and Tax Statement. The IRS lets your employer deliver it electronically only with your consent; otherwise it comes on paper. Employers release W-2s by January 31.">
          <InfoOutlined fontSize="small" color="action" />
        </Tooltip>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 1 }}>{success}</Alert>}

      {status === 'none' && (
        <Stack spacing={1} sx={{ mb: 2, alignItems: 'flex-start' }}>
          <Alert severity="info" sx={{ width: '100%' }}>
            Your W-2 is delivered on paper. Consent to electronic delivery to get each year's W-2 here as a PDF as soon as it is released.
          </Alert>
          <Button variant="contained" size="small" onClick={openConsent}>Set up electronic W-2 delivery</Button>
        </Stack>
      )}
      {status === 'consented' && (
        <Stack spacing={0.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip color="success" size="small" label={`Electronic delivery — consented on ${fmtDate(consent.consented_at)}`} />
            <Button size="small" color="inherit" onClick={() => setWithdrawOpen(true)}>Withdraw consent</Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Want a paper copy as well? Ask your payroll administrator — that does not withdraw your consent.
          </Typography>
        </Stack>
      )}
      {status === 'withdrawn' && (
        <Stack spacing={0.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Paper delivery — consent withdrawn on ${fmtDate(consent.withdrawn_at)}`} />
            <Button size="small" variant="outlined" onClick={openConsent}>Consent again</Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            W-2s furnished to you before you withdrew stay available below; later ones come on paper.
          </Typography>
        </Stack>
      )}

      {w2s.length === 0 ? (
        status === 'consented' && (
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">No W-2s have been released to you yet.</Typography>
          </Paper>
        )
      ) : isMobile ? (
        <Stack spacing={1}>
          {w2s.map(w => (
            <Card key={w.id} variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2">{w.tax_year} Form W-2{copyLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">Available since {fmtDate(w.furnished_at)}</Typography>
                    <Typography variant="caption" color="text.secondary">{keepNote(w.tax_year)}</Typography>
                  </Box>
                  <Tooltip title={enabled ? 'Download PDF' : 'Download PDF (copy for your records)'}>
                    <IconButton color="primary" onClick={() => handleDownload(w.id)}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tax Year</TableCell>
                <TableCell>Available since</TableCell>
                <TableCell>Download</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {w2s.map(w => (
                <TableRow key={w.id} hover>
                  <TableCell>
                    {w.tax_year}{copyLabel}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{keepNote(w.tax_year)}</Typography>
                  </TableCell>
                  <TableCell>{fmtDate(w.furnished_at)}</TableCell>
                  <TableCell>
                    <Tooltip title={enabled ? 'Download PDF' : 'Download PDF (copy for your records)'}>
                      <IconButton size="small" color="primary" onClick={() => handleDownload(w.id)}>
                        <Download />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={consentOpen} onClose={() => setConsentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Electronic W-2 delivery consent</DialogTitle>
        <DialogContent dividers>
          {consent && !enabled && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Your employer is not yet delivering the official Form W-2 electronically. Until it does you will still get your official W-2 on paper; the PDF here is a copy for your records. Your consent applies once electronic delivery is turned on.
            </Alert>
          )}
          {W2_DISCLOSURE(employer, enabled).map(s => (
            <Box key={s.heading} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2">{s.heading}</Typography>
              <Typography variant="body2">{s.text}</Typography>
            </Box>
          ))}
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" size="small" startIcon={<Download />} onClick={handleSample}>Download sample PDF</Button>
            {!!sampleToken && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Sample downloaded. If it opened on this device, you can receive your W-2 this way.
              </Typography>
            )}
            {sampleError && <Alert severity="error" sx={{ mt: 1 }}>{sampleError}</Alert>}
          </Box>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={checked} disabled={!sampleToken} onChange={(e) => setChecked(e.target.checked)} />}
              label={enabled
                ? 'I have read the disclosures above and consent to receive my Form W-2 electronically through HourTrax instead of on paper.'
                : 'I have read the disclosures above and consent to electronic delivery of my Form W-2 through HourTrax once my employer turns it on. Until then the PDF is a copy and I will still receive my official W-2 on paper.'}
            />
            {!sampleToken && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Download the sample PDF first.</Typography>
            )}
          </Box>
          {dialogError && <Alert severity="error" sx={{ mt: 1 }}>{dialogError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!sampleToken || !checked || saving} onClick={handleConsent}>I consent</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={withdrawOpen} onClose={() => setWithdrawOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Withdraw consent?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Future W-2s will be furnished on paper. This takes effect immediately. W-2s already furnished to you electronically stay available here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" disabled={saving} onClick={handleWithdraw}>Withdraw consent</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
